
import { supabase } from '@/integrations/supabase/client';
import { fetchPlanById } from './plansService';
import { pricingPlans } from '@/config/pricingPlans';

/**
 * Fetch order details from the database
 */
export const fetchOrderDetails = async (orderId: string) => {
  try {
    console.log('Fetching order details from database for ID:', orderId);
    
    // Try to get exact match first
    let { data, error } = await supabase
      .from('payment_orders')
      .select('*')
      .eq('order_id', orderId)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching order details:', error);
      return null;
    }
    
    // If no exact match and order ID is long enough, try pattern matching
    if (!data && orderId && orderId.length > 5) {
      console.log('No exact match found, trying pattern match for order ID:', orderId);
      
      // When doing pattern matching, use a more robust approach
      const { data: patternData, error: patternError } = await supabase
        .from('payment_orders')
        .select('*')
        .ilike('order_id', `%${orderId.substring(6, 16)}%`)  // Use the most distinctive portion of the ID
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (patternError) {
        console.error('Error in pattern search:', patternError);
      } else if (patternData) {
        console.log('Found order via pattern match:', patternData);
        
        // If product_name is not set, try to retrieve it from payment_details
        if (!patternData.product_name || patternData.product_name === 'N/A' || patternData.product_name === 'Subscription') {
          await updateProductNameFromDetails(patternData);
        }
        
        return patternData;
      } else {
        console.log('No pattern matches found for order ID:', orderId);
      }
    }
    
    // For exact matches, also ensure product_name is set correctly
    if (data) {
      // If product_name is not set, try to retrieve it from payment_details
      if (!data.product_name || data.product_name === 'N/A' || data.product_name === 'Subscription') {
        await updateProductNameFromDetails(data);
      }
      
      console.log("Order details found by exact match:", data);
      return data;
    } else {
      console.log("No order details found for ID:", orderId);
      return null;
    }
  } catch (error) {
    console.error('Error in fetchOrderDetails:', error);
    return null;
  }
};

/**
 * Update product name based on order details
 */
const updateProductNameFromDetails = async (orderData) => {
  let updatedProductName = null;
  const reasons = [];
  
  // First try to get product name from payment_details.link_meta.product_name
  if (orderData.payment_details?.link_meta?.product_name) {
    updatedProductName = orderData.payment_details.link_meta.product_name;
    reasons.push('from payment_details.link_meta.product_name');
  } 
  // Then try from payment_details.original_payload.product_name
  else if (orderData.payment_details?.original_payload?.productName) {
    updatedProductName = orderData.payment_details.original_payload.productName;
    reasons.push('from payment_details.original_payload.productName');
  }
  // Then try from payment_details.link_meta.plan
  else if (orderData.payment_details?.link_meta?.plan) {
    const planType = orderData.payment_details.link_meta.plan.toLowerCase();
    try {
      updatedProductName = await getPlanDisplayName(planType);
      reasons.push(`derived from payment_details.link_meta.plan "${planType}"`);
    } catch (error) {
      console.error('Error getting plan display name:', error);
    }
  }
  // Then try from payment_details.purpose
  else if (orderData.payment_details?.link_purpose) {
    const purpose = orderData.payment_details.link_purpose.toLowerCase();
    if (purpose.includes('professional')) {
      updatedProductName = 'Professional Plan';
      reasons.push('extracted from payment_details.link_purpose containing "professional"');
    } else if (purpose.includes('starter')) {
      updatedProductName = 'Starter Plan';
      reasons.push('extracted from payment_details.link_purpose containing "starter"');
    } else if (purpose.includes('basic')) {
      updatedProductName = 'Basic Plan';
      reasons.push('extracted from payment_details.link_purpose containing "basic"');
    }
  }
  // Finally fallback to amount-based inference
  else if (orderData.amount) {
    const details = await getPlanDetailsByAmount(orderData.amount);
    updatedProductName = details.displayName;
    reasons.push(`inferred from amount ${orderData.amount}`);
  }
  
  // If we found a product name and it's different from the current one, update it
  if (updatedProductName && updatedProductName !== orderData.product_name) {
    console.log(`Updating product name from "${orderData.product_name}" to "${updatedProductName}" (${reasons.join(', ')})`);
    
    // Update the product name in the database
    const { error: updateError } = await supabase
      .from('payment_orders')
      .update({ product_name: updatedProductName })
      .eq('id', orderData.id);
      
    if (updateError) {
      console.error('Error updating product name:', updateError);
    } else {
      console.log(`Successfully updated product name to "${updatedProductName}"`);
      orderData.product_name = updatedProductName;
    }
  } else if (updatedProductName) {
    console.log(`Product name "${updatedProductName}" already set correctly`);
  } else {
    console.log('Could not determine product name from order details');
  }
};

/**
 * Get plan details by plan ID
 * This can be extended as new plans are added
 */
export const getPlanDetails = async (planId) => {
  try {
    // First try to get the plan from the database
    const plan = await fetchPlanById(planId);
    
    if (plan) {
      return {
        displayName: `${plan.title} Plan`,
        amount: plan.monthlyAmount,
        computeCredits: plan.computeCredits,
        discountPercentage: plan.discountPercentage,
        features: plan.features.filter(f => f.included).map(f => f.name)
      };
    }
    
    // If no plan found in the database, use fallback data
    // Normalize plan ID to lowercase for comparison
    const normalizedPlanId = (planId || '').toLowerCase();
    
    // Define plan details in a central location with compute credits
    const planDetailsMap = {
      'basic': {
        displayName: 'Basic Plan',
        amount: 250,
        computeCredits: 1000000,
        discountPercentage: 72,
        features: ['15+ LLM models', 'Unified conversation history', 'PDF and image upload']
      },
      'starter': {
        displayName: 'Starter Plan',
        amount: 500,
        computeCredits: 2500000,
        discountPercentage: 67,
        features: ['20+ LLM models', 'Unified conversation history', 'PDF and image upload', 'Access to premium models', 'Early access to new models']
      },
      'professional': {
        displayName: 'Professional Plan',
        amount: 2000,
        computeCredits: 10000000,
        discountPercentage: 33,
        features: ['20+ LLM models', 'Unlimited conversation history', 'PDF and image upload', 'Access to all premium models', 'Early access to new models', 'Priority support']
      }
    };
    
    // Return the plan details or a default
    return planDetailsMap[normalizedPlanId] || {
      displayName: normalizedPlanId ? `${normalizedPlanId.charAt(0).toUpperCase() + normalizedPlanId.slice(1)} Plan` : 'Subscription',
      amount: 0,
      computeCredits: 0,
      discountPercentage: 0,
      features: []
    };
  } catch (error) {
    console.error('Error in getPlanDetails:', error);
    
    // Return a default plan on error
    return {
      displayName: planId ? `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan` : 'Subscription',
      amount: 0,
      computeCredits: 0,
      discountPercentage: 0,
      features: []
    };
  }
};

/**
 * Get plan display name based on plan ID
 */
export const getPlanDisplayName = async (planId) => {
  const details = await getPlanDetails(planId);
  return details.displayName;
};

/**
 * Get plan details by amount
 */
export const getPlanDetailsByAmount = async (amount) => {
  try {
    // First try to get from database
    const { data, error } = await supabase
      .from('plans')
      .select('plan_id, title, monthly_amount')
      .eq('monthly_amount', amount)
      .maybeSingle();
    
    if (error) {
      throw error;
    }
    
    if (data) {
      return {
        id: data.plan_id,
        displayName: `${data.title} Plan`,
        amount: data.monthly_amount
      };
    }
    
    // Fallback to static data
    // Define each plan with its amount
    const plans = [
      { id: 'basic', displayName: 'Basic Plan', amount: 750 },
      { id: 'starter', displayName: 'Starter Plan', amount: 1600 },
      { id: 'professional', displayName: 'Professional Plan', amount: 3250 }
    ];
    
    // Find the plan with the matching amount
    const matchedPlan = plans.find(plan => plan.amount === amount);
    
    // Return the matched plan or a default
    return matchedPlan || { 
      id: 'custom', 
      displayName: 'Subscription', 
      amount: amount 
    };
  } catch (error) {
    console.error('Error in getPlanDetailsByAmount:', error);
    
    // Return a generic plan on error
    return { 
      id: 'custom', 
      displayName: 'Subscription', 
      amount: amount 
    };
  }
};

/**
 * Send order parameters to backend for storage
 */
export const sendOrderParamsToBackend = async (orderParams: { 
  order_id: string; 
  plan: string; 
  status?: string;
  email?: string;
  name?: string;
  phone?: string;
}) => {
  try {
    console.log('Sending order parameters to backend for storage:', orderParams);
    
    // Get the plan details based on the plan ID
    const planId = orderParams.plan.toLowerCase();
    const planDetails = await getPlanDetails(planId);
    
    console.log(`Mapped plan "${planId}" to product name: "${planDetails.displayName}"`);
    
    const response = await supabase.functions.invoke('create-payment-order', {
      body: {
        urlParams: {
          order_id: orderParams.order_id,
          plan: orderParams.plan,
          status: orderParams.status || 'PENDING',
          productName: planDetails.displayName, // Explicitly include product name
          email: orderParams.email || '', // Include email
          name: orderParams.name || '', // Include customer name
          phone: orderParams.phone || '' // Include phone number
        }
      }
    });
    
    console.log('Response from backend:', response);
    
    // Properly handle the response object for TypeScript compatibility
    if ('error' in response && response.error) {
      console.error('Error from backend:', response.error);
      return { error: response.error };
    }
    
    return response;
  } catch (err) {
    console.error('Sending order parameters to backend:', err);
    return { error: err };
  }
};
